import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, Loader2, CheckCircle2, MessageSquare } from "lucide-react";
import { CONTACT, SERVICES_INCLUDE } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactForm = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [fromQuote, setFromQuote] = useState(false);
  const estimateRef = useRef({});
  const messageRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      const d = e.detail || {};
      estimateRef.current = {
        frequency: d.frequency,
        square_footage: d.square_footage,
        estimated_monthly: d.estimated_monthly,
      };
      setForm((f) => ({ ...f, message: d.message || f.message, service: f.service || "Commercial Cleaning" }));
      setFromQuote(true);
      setTimeout(() => {
        messageRef.current?.classList.add("ring-2", "ring-[#e02329]");
        setTimeout(() => messageRef.current?.classList.remove("ring-2", "ring-[#e02329]"), 2500);
      }, 600);
    };
    window.addEventListener("prefill-quote", handler);
    return () => window.removeEventListener("prefill-quote", handler);
  }, []);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email and phone.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        source: fromQuote ? "quote" : "estimate",
        ...estimateRef.current,
      });
      setDone(true);
      toast.success("Estimate request sent! We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
      setFromQuote(false);
      estimateRef.current = {};
    } catch (err) {
      toast.error("Something went wrong. Please call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 sm:py-32 bg-[#1c4f9c] relative overflow-hidden">
      <div className="absolute inset-0 grain-overlay opacity-[0.08]" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-14">
        {/* Info */}
        <div className="text-white">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#ff6b70] mb-3">Free Estimate</p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
            Request your free commercial cleaning estimate
          </h2>
          <p className="mt-5 text-base text-blue-100/85 leading-relaxed max-w-lg">
            Tell us about your facility and our team will build a customized cleaning program with transparent pricing. No obligation.
          </p>

          <div className="mt-10 space-y-5">
            <a href={CONTACT.phoneHref} data-testid="contact-phone" className="flex items-center gap-4 group">
              <span className="w-12 h-12 flex items-center justify-center bg-white/10 rounded-sm group-hover:bg-[#e02329] transition-colors duration-200">
                <Phone size={20} />
              </span>
              <div>
                <p className="text-xs text-blue-200 uppercase tracking-wide">Call Now</p>
                <p className="text-lg font-bold">{CONTACT.phone}</p>
              </div>
            </a>
            <a href={CONTACT.smsHref} data-testid="contact-sms" className="flex items-center gap-4 group">
              <span className="w-12 h-12 flex items-center justify-center bg-white/10 rounded-sm group-hover:bg-[#e02329] transition-colors duration-200">
                <MessageSquare size={20} />
              </span>
              <div>
                <p className="text-xs text-blue-200 uppercase tracking-wide">Text Us</p>
                <p className="text-lg font-bold">{CONTACT.phone}</p>
              </div>
            </a>
            <a href={CONTACT.emailHref} data-testid="contact-email" className="flex items-center gap-4 group">
              <span className="w-12 h-12 flex items-center justify-center bg-white/10 rounded-sm group-hover:bg-[#e02329] transition-colors duration-200">
                <Mail size={20} />
              </span>
              <div>
                <p className="text-xs text-blue-200 uppercase tracking-wide">Email Us</p>
                <p className="text-lg font-bold">{CONTACT.email}</p>
              </div>
            </a>
            <div className="flex items-center gap-4">
              <span className="w-12 h-12 flex items-center justify-center bg-white/10 rounded-sm">
                <MapPin size={20} />
              </span>
              <div>
                <p className="text-xs text-blue-200 uppercase tracking-wide">Service Area</p>
                <p className="text-lg font-bold">Northeast Florida</p>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-sm p-8 sm:p-10">
          {done ? (
            <div data-testid="contact-success" className="h-full flex flex-col items-center justify-center text-center py-12">
              <CheckCircle2 size={56} className="text-[#1c4f9c]" />
              <h3 className="mt-5 text-2xl font-bold text-[#1c4f9c]">Thank you!</h3>
              <p className="mt-2 text-slate-600">Your request has been received. Our team will reach out within one business day.</p>
              <button onClick={() => setDone(false)} className="mt-6 text-sm font-bold text-[#e02329]">Submit another request</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5">
              {fromQuote && (
                <div data-testid="quote-attached-banner" className="flex items-center gap-2 bg-[#1c4f9c]/5 border border-[#1c4f9c]/20 text-[#1c4f9c] text-sm font-semibold rounded-sm px-4 py-3">
                  <CheckCircle2 size={18} className="text-[#e02329] shrink-0" /> Your estimate is attached below — just add your details.
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold text-[#1c4f9c] mb-2">Full Name *</label>
                  <input data-testid="contact-name-input" value={form.name} onChange={update("name")} className="w-full border border-slate-300 rounded-sm px-4 py-3 focus:border-[#1c4f9c] focus:ring-1 focus:ring-[#1c4f9c] outline-none" placeholder="John Smith" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#1c4f9c] mb-2">Phone *</label>
                  <input data-testid="contact-phone-input" value={form.phone} onChange={update("phone")} className="w-full border border-slate-300 rounded-sm px-4 py-3 focus:border-[#1c4f9c] focus:ring-1 focus:ring-[#1c4f9c] outline-none" placeholder="(786) 918-1367" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#1c4f9c] mb-2">Email *</label>
                <input data-testid="contact-email-input" type="email" value={form.email} onChange={update("email")} className="w-full border border-slate-300 rounded-sm px-4 py-3 focus:border-[#1c4f9c] focus:ring-1 focus:ring-[#1c4f9c] outline-none" placeholder="you@company.com" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#1c4f9c] mb-2">Service Needed</label>
                <select data-testid="contact-service-select" value={form.service} onChange={update("service")} className="w-full border border-slate-300 rounded-sm px-4 py-3 focus:border-[#1c4f9c] focus:ring-1 focus:ring-[#1c4f9c] outline-none bg-white">
                  <option value="">Select a service</option>
                  {SERVICES_INCLUDE.map((s) => (
                    <option key={s.title} value={s.title}>{s.title}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#1c4f9c] mb-2">Tell us about your facility</label>
                <textarea ref={messageRef} data-testid="contact-message-input" value={form.message} onChange={update("message")} rows={4} className="w-full border border-slate-300 rounded-sm px-4 py-3 focus:border-[#1c4f9c] focus:ring-1 focus:ring-[#1c4f9c] outline-none resize-none transition-shadow" placeholder="Facility type, size, frequency, special requests..." />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-btn"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#e02329] text-white font-bold py-4 px-8 rounded-sm hover:bg-[#c81e24] transition-colors duration-200 disabled:opacity-70"
              >
                {loading ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
                {loading ? "Sending..." : "Request a Free Estimate"}
              </button>
              <p className="text-center text-xs text-slate-400">
                No obligation. A cleaning specialist will respond within one business day.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
